import { useEffect, useState } from "react";
import Button from "components/Button";
import Select from "components/Select";
import * as S from "./styles";
import { useDropzone } from "react-dropzone";
import { ReactComponent as UploadImageIcon } from "assets/upload-picture.svg";
import { ReactComponent as TrashIcon } from "assets/delete.svg";

import {
  AttachmentCategories,
  AttachmentCategory,
} from "./data";
import { fireToast } from "services/sweetAlertConfig";

import { useManualOccurrence } from "contexts/register/ManualOccurrence";
import OccurrenceManagementService, {
  ISendManualOccurrencePicture,
} from "services/OccurrenceManagementService";
import NoSelectedReportError from "../NoSelectedReportError";

type FilePreview = {
  preview: string;
};

export default function AttachmentsTab() {
  // retrieving current occurrence to edit data
  const { occurrenceToEdit, selectedReport, refetch } = useManualOccurrence();

  // state to store the category of the photo being sent
  const [category, setCategory] = useState<
    AttachmentCategory.Patrimony | AttachmentCategory.Vehicle | 'no_category'
  >('no_category');

  const [isRequesting, setIsRequesting] = useState<boolean>();
  const [files, setFiles] = useState<File[]>([]);
  const [filePreview, setFilePreview] = useState<FilePreview[]>([]);

  // dropzone config to drop files on the file input
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles: any) => {
      setFiles(prev => [...prev, ...acceptedFiles]);
    },
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/jpg": [],
    },
    multiple: true,
    maxFiles: 5,
  });

  useEffect(() => {
    setFilePreview(
      files.map((file: Blob | MediaSource) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      )
    );
  }, [files])

  // type of the occurrence
  const occurrenceType =
    occurrenceToEdit.patrimonialType === 1 ? "patrimony" : "vehicle";

  // list of possible picture categories based on occurrence type
  const categoryList =
    occurrenceType === "patrimony"
      ? AttachmentCategories.patrimony
      : AttachmentCategories.vehicle;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!files || files.length === 0) {
      return fireToast("Um ou mais arquivos não foram selecionados");
    }

    if (!selectedReport?.id) {
      return fireToast("Nenhum agente foi associado a esta ocorrencia!");
    }

    setIsRequesting(true);

    // gets the current occurrence attachments
    const attachments = selectedReport?.attachments ?? [];

    // check the number of photos of that category in the current occurrence
    const currentCategoryPicturesLength = attachments.filter(
      (attachment) => attachment.category === category
    ).length;


    // creates the body of the picture sent
    const body: ISendManualOccurrencePicture = {
      category,
      reportId: selectedReport?.id ?? '',
      picture: {
        cameraData: files,
        caption: category ?? 'Foto não identificada',
        location: files.map((_, index) => `${category}_${currentCategoryPicturesLength + (index + 1)}`)
      },
    };

    try {
      await OccurrenceManagementService.send(body);
      fireToast("Foto enviada com sucesso");
      refetch();
    } catch {
      fireToast("Ocorreu um erro ao enviar os anexos para a central.");
    } finally {
      setIsRequesting(false);
    }
  }

  function handleRemovePicture(fileIndex: number) {
    setFiles((prev) => {
      return prev.filter((_, index) => index !== fileIndex)
    })
  }

  // variable check if there is a policeman attached to this occurrence
  const policemanId =
    selectedReport?.superficialPoliceman?.id ||
    selectedReport?.policeman?.user?.id;

  return (
    <S.Wrapper>
      {!selectedReport && <NoSelectedReportError />}

      {selectedReport && policemanId && (
        <form onSubmit={handleSubmit}>
          <S.Column>
            {filePreview.length > 0 && (
              <S.PreviewContainer>
                {filePreview.map((item, index) => (
                  <div className="image-preview-container">
                    <button className="trash-icon" onClick={() => handleRemovePicture(index)} type="button">
                      <TrashIcon />
                    </button>
                    <S.ImagePreview src={item.preview} />
                  </div>
                ))}
              </S.PreviewContainer>
            )}

            <S.LabelButton {...getRootProps()} isFileSelected={files.length > 0}>
              <input autoComplete="off" {...getInputProps()} />
              <div>
                <UploadImageIcon />
                <p>Enviar foto</p>
                <p>Maximo de 5 fotos por vez</p>
              </div>
            </S.LabelButton>
          </S.Column>
          <S.Column>
            <S.FormGroup>
              <Select
                id="attachment_type"
                name="attachment_type"
                onChange={(event) => {
                  if (event.target.value === "default") {
                    return setCategory('no_category');
                  }

                  setCategory(
                    event.target.value as
                    | AttachmentCategory.Patrimony
                    | AttachmentCategory.Vehicle
                  );
                }}
                required
                label="Categoria da foto (Opicional)"
              >
                <option value="default">Selecione um tipo</option>

                {categoryList.map((option, key) => (
                  <option key={key} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </S.FormGroup>

            <S.FormGroup>
              <Button
                title={isRequesting ? "Enviando foto..." : "Registrar anexo"}
                type="submit"
                disabled={isRequesting}
              />
            </S.FormGroup>
          </S.Column>
        </form>
      )}
    </S.Wrapper>
  );
}
